// src/utils/tracking.js

export const CONSENT_KEY = "3dspot_cookie_consent_v1";

const META_PIXEL_ID = "1268142450730060";
const GA_MEASUREMENT_ID = "G-5EFL5K07NB";

// Google Ads destination used by the live Google tag.
const GOOGLE_ADS_ID = "AW-11017539986";
const GOOGLE_ADS_FREE_QUOTE_LABEL = "FV6kCKaBhsgcEJKjyYUp";

let googleLoaded = false;
let metaLoaded = false;
let lastPageViewPath = "";

const ATTRIBUTION_STORAGE_KEY = "3dspot_google_ads_attribution_v1";

const ATTRIBUTION_QUERY_MAP = {
  gclid: "gclid",
  gbraid: "gbraid",
  wbraid: "wbraid",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
};

export const captureAdAttribution = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};

    Object.entries(ATTRIBUTION_QUERY_MAP).forEach(([queryKey, storageKey]) => {
      const value = params.get(queryKey);
      if (value) captured[storageKey] = value;
    });

    if (!Object.keys(captured).length) return getStoredAdAttribution();

    const previous = getStoredAdAttribution();
    const hasNewClickId = Boolean(captured.gclid || captured.gbraid || captured.wbraid);
    // A fresh ad click replaces the old click identity rather than mixing
    // campaign metadata from different visits.
    const next = {
      ...(hasNewClickId ? {} : previous),
      ...captured,
      capturedAt: new Date().toISOString(),
    };
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch (error) {
    console.warn("Could not capture ad attribution", error);
    return {};
  }
};

export const getStoredAdAttribution = () => {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    const capturedAt = parsed?.capturedAt ? new Date(parsed.capturedAt).getTime() : 0;
    const maxAge = 90 * 24 * 60 * 60 * 1000;

    if (capturedAt && Date.now() - capturedAt > maxAge) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }

    return parsed;
  } catch {
    return {};
  }
};

const injectScript = (id, src) => {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; Max-Age=0; path=/`;
  document.cookie = `${name}=; Max-Age=0; path=/; domain=.3dspot.io`;
  document.cookie = `${name}=; Max-Age=0; path=/; domain=www.3dspot.io`;
};

export const deleteTrackingCookies = () => {
  [
    "_ga",
    "_gid",
    "_gat",
    "_gcl_au",
    "_gcl_aw",
    "_gac",
    "_fbp",
    "_fbc",
  ].forEach(deleteCookie);
};

export const getStoredConsent = () => {
  try {
    const saved = localStorage.getItem(CONSENT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const consent = getStoredConsent();
  return !!consent?.analytics;
};

export const hasMarketingConsent = () => {
  const consent = getStoredConsent();
  return !!consent?.marketing;
};

export const setupGoogleConsentDefaults = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
};

export const updateGoogleConsent = (consent) => {
  setupGoogleConsentDefaults();

  window.gtag("consent", "update", {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
  });
};

export const loadGoogleTracking = (consent) => {
  if (!consent.analytics && !consent.marketing) return;

  setupGoogleConsentDefaults();
  updateGoogleConsent(consent);

  if (!googleLoaded) {
    googleLoaded = true;

    const firstGoogleId = consent.analytics
      ? GA_MEASUREMENT_ID
      : GOOGLE_ADS_ID;

    injectScript(
      "google-gtag-js",
      `https://www.googletagmanager.com/gtag/js?id=${firstGoogleId}`
    );

    window.gtag("js", new Date());
  }

  if (consent.analytics && GA_MEASUREMENT_ID) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false,
    });
  }

  if (consent.marketing && GOOGLE_ADS_ID) {
    window.gtag("config", GOOGLE_ADS_ID);
  }
};

export const loadMetaPixel = () => {
  if (metaLoaded) return;

  metaLoaded = true;

  const initMetaPixel = (f, b, e, v, n, t, s) => {
    if (f.fbq) return;

    n = f.fbq = function () {
      if (n.callMethod) {
        n.callMethod.apply(n, arguments);
      } else {
        n.queue.push(arguments);
      }
    };

    if (!f._fbq) f._fbq = n;

    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];

    t = b.createElement(e);
    t.async = true;
    t.src = v;

    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  };

  initMetaPixel(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js"
  );

  window.fbq("consent", "grant");
  window.fbq("init", META_PIXEL_ID);
};

export const applyConsent = (consent) => {
  if (!consent) return;

  setupGoogleConsentDefaults();
  updateGoogleConsent(consent);

  if (consent.analytics || consent.marketing) {
    loadGoogleTracking(consent);
  }

  if (consent.marketing) {
    loadMetaPixel();
  }

  if (!consent.analytics && !consent.marketing) {
    deleteTrackingCookies();

    if (window.fbq) {
      window.fbq("consent", "revoke");
    }
  }
};

export const trackPageView = () => {
  const consent = getStoredConsent();
  if (!consent) return;

  const path = window.location.pathname + window.location.search;

  if (path === lastPageViewPath) return;
  lastPageViewPath = path;

  if (consent.analytics && window.gtag) {
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: path,
    });
  }

  if (consent.marketing && window.fbq) {
    window.fbq("track", "PageView");
  }
};

export const trackLead = (params = {}) => {
  const consent = getStoredConsent();

  if (!consent?.marketing) return;

  const leadParams = {
    value: params.value ?? 1.0,
    currency: params.currency || "INR",
    lead_type: params.lead_type || "free_quote",
    ...params,
  };

  if (window.fbq) {
    window.fbq("track", "Lead", leadParams);
  }

  if (window.gtag) {
    // GA4 lead event
    window.gtag("event", "generate_lead", leadParams);

    // Google Ads Free Quote Submitted conversion
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${GOOGLE_ADS_FREE_QUOTE_LABEL}`,
      value: leadParams.value,
      currency: leadParams.currency,
    });
  }
};

export const trackPurchase = ({ value, currency = "USD", transactionId }) => {
  const consent = getStoredConsent();

  if (consent?.marketing && window.fbq) {
    window.fbq("track", "Purchase", {
      value,
      currency,
    });
  }

  if (consent?.marketing && window.gtag) {
    window.gtag("event", "purchase", {
      value,
      currency,
      transaction_id: transactionId,
    });
  }
};

export const trackViewContent = (params = {}) => {
  const consent = getStoredConsent();

  if (consent?.marketing && window.fbq) {
    window.fbq("track", "ViewContent", params);
  }
};

export const trackCustomEvent = (eventName, params = {}) => {
  const consent = getStoredConsent();

  if (consent?.marketing && window.fbq) {
    window.fbq("trackCustom", eventName, params);
  }
};